import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import { Message } from '@patsnap/tahiti-core/common/message';
import { feedbackApi } from 'library-@/apis/feedback';
import { FeedbackStatus, FeedbackSuffix } from './feedback.type';
import { I18nService } from '@analytics/shared/services/i18n.interface'
import type VueI18n from 'vue-i18n';

export const FeedbackService = Symbol('FeedbackService');

export interface FeedbackService {
    status: FeedbackStatus;
    onStatusChange: Event<FeedbackStatus>;
    init: (type: string, params: { [key in string]: any }) => void;
    handleLike: () => void;
    handleDislike: (callback: () => void) => void;
    submit: (typeSuffix?: FeedbackSuffix, comment?: string) => void; // 后端支持type长度最大为16个字符
}

@injectable()
export class FeedbackServiceImpl implements FeedbackService {
    private _onStatusChangeEmitter = new Emitter<FeedbackStatus>();
    readonly onStatusChange = this._onStatusChangeEmitter.event;

    public status: FeedbackStatus = FeedbackStatus.DEFAULT;
    private _type = '';
    private _params = {}

    constructor(
        @inject(Message)
        private _message: Message,
        @inject(I18nService)
        private _i18n: VueI18n
    ) { }

    private get _isLike() {
        return this.status === FeedbackStatus.LIKE;
    }

    private get _isDislike() {
        return this.status === FeedbackStatus.DISLIKE;
    }

    private get _isDefault() {
        return this.status === FeedbackStatus.DEFAULT;
    }

    private _updateStatus(status: FeedbackStatus) {
        this.status = status;
        this._onStatusChangeEmitter.fire(this.status);
    }

    public init(type: string, params: { [key in string]: any }) {
        this._type = type;
        this._params = params;
    }

    public submit(typeSuffix = FeedbackSuffix.LIKE, comment = '') {
        const type = `${this._type}-${typeSuffix}`;

        this._updateStatus(FeedbackStatus[typeSuffix === FeedbackSuffix.LIKE ? 'LIKE' : 'DISLIKE']);

        feedbackApi({
            ...this._params,
            type,
            comment,
        });

        this._message.closeAll();
        this._message.success({
            message: this._i18n.t('thanksTips') as string,
            showClose: true,
        })
    };

    public handleLike() {
        if (this._isDislike) return;
        if (this._isLike) {
            this._updateStatus(FeedbackStatus.DEFAULT);
            return;
        }

        this.submit(FeedbackSuffix.LIKE);
    }

    public handleDislike(callback: () => void) {
        if (this._isDefault) {
            callback();
        }

        if (this._isDislike) {
            this._updateStatus(FeedbackStatus.DEFAULT);
        }
    }
}